<template>
  <woot-modal :show="true" :on-close="onClose" :show-close-button="true" :close-on-backdrop-click="true" size="medium">
    <div class="qt-container">
      <div class="modal-header">
        <div class="header-content">
          <div class="title-group"><h1 class="modal-title">Criar tarefa nesta conversa</h1></div>
        </div>
      </div>
      <div class="qt-body">
        <div v-if="conversationDisplayId" class="link-pill">
          <span class="i-ri-message-2-line size-4" />
          <span>Vinculada a conversa <strong>#{{ conversationDisplayId }}</strong></span>
        </div>
        <div v-if="availableTemplates.length > 0" class="template-bar">
          <span class="i-ri-file-text-line size-3.5 text-n-slate-10" />
          <span class="tplabel">Aplicar template:</span>
          <select v-model="selectedTemplateId" class="form-input form-input-sm" @change="applyTemplate">
            <option :value="null">- nenhum -</option>
            <option v-for="t in availableTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
        </div>
        <form class="qt-form" @submit.prevent="submit">
          <div class="form-group">
            <label>Titulo <span class="required">*</span></label>
            <input ref="titleInput" v-model="form.title" type="text" required class="form-input" placeholder="O que precisa ser feito?" />
          </div>
          <div class="form-group">
            <label>Descricao</label>
            <textarea v-model="form.description" rows="2" class="form-input" placeholder="Detalhes (opcional)"></textarea>
          </div>
          <div class="form-row form-row-3">
            <div class="form-group">
              <label>Prioridade</label>
              <select v-model="form.priority" class="form-input">
                <option value="none">Sem prioridade</option>
                <option value="low">Baixa</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
            <div class="form-group">
              <label>Status</label>
              <select v-model="form.status_key" class="form-input">
                <option value="pending">Pendente</option>
                <option value="in_progress">Em andamento</option>
                <option value="done">Concluida</option>
              </select>
            </div>
            <div class="form-group">
              <label>Atribuido a</label>
              <select v-model="form.assigned_to_user_id" class="form-input">
                <option :value="null">- ninguem -</option>
                <option v-for="u in agents" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>Vencimento</label>
            <date-picker
              v-model:value="form.due_at_local"
              type="datetime"
              format="DD/MM/YYYY HH:mm"
              value-type="format"
              :lang="ptBR"
              :clearable="true"
              :show-second="false"
              :minute-step="5"
              placeholder="Selecione data e hora" :append-to-body="true"
              class="qt-datepicker"
            />
          </div>
          <div v-if="availableLabels.length > 0" class="form-group">
            <label>Etiquetas</label>
            <div class="label-chips">
              <button v-for="l in availableLabels" :key="l.id" type="button" :class="['label-chip', selectedLabelIds.includes(l.id) && 'label-chip-active']" :style="selectedLabelIds.includes(l.id) ? { backgroundColor: l.color + '33', color: l.color, borderColor: l.color } : { color: l.color }" @click="toggleLabel(l.id)">{{ l.name }}</button>
            </div>
          </div>
          <div class="form-group">
            <label>Subtarefas</label>
            <div v-for="(st, i) in subtasks" :key="i" class="subtask-item">
              <span class="i-ri-checkbox-line size-3.5 text-n-slate-10" />
              <input v-model="st.title" type="text" class="form-input form-input-flat" placeholder="Item" />
              <button type="button" class="icon-btn-sm" @click="subtasks.splice(i, 1)"><span class="i-ri-close-line size-3.5" /></button>
            </div>
            <button type="button" class="add-sub-btn" @click="subtasks.push({ title: '', position: subtasks.length })">
              <span class="i-ri-add-line size-3.5" /> Adicionar subtarefa
            </button>
          </div>
          <div class="form-row form-row-2">
            <div class="form-group">
              <label>Lembretes</label>
              <div class="reminders">
                <label v-for="opt in reminderOptions" :key="opt.value" class="rmd-checkbox">
                  <input type="checkbox" :checked="form.reminder_offsets.includes(opt.value)" @change="toggleReminder(opt.value)" />
                  <span>{{ opt.label }}</span>
                </label>
              </div>
            </div>
            <div class="form-group">
              <label>Repetir</label>
              <div class="recurrence">
                <label class="rmd-checkbox"><input type="checkbox" v-model="recurrenceEnabled" /><span>Sim</span></label>
                <select v-if="recurrenceEnabled" v-model="recurrenceFrequency" class="form-input form-input-sm">
                  <option value="daily">Diario</option>
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensal</option>
                </select>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="qt-btn qt-btn-secondary" @click="onClose">Cancelar</button>
        <button type="submit" class="qt-btn qt-btn-primary" :disabled="saving || !form.title.trim()" @click="submit">
          <span v-if="saving" class="i-ri-loader-2-line size-4 animate-spin" />
          <span v-else class="i-ri-check-line size-4" />
          <span>Criar tarefa</span>
        </button>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TasksAPI from 'dashboard/api/tasks';
import TaskSubtasksAPI from 'dashboard/api/taskSubtasks';
import TaskLabelsAPI from 'dashboard/api/taskLabels';
import TaskTemplatesAPI from 'dashboard/api/taskTemplates';
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';

export default {
  name: 'QuickCreateTaskDialog',
  components: { DatePicker },
  props: {
    conversationDisplayId: { type: Number, default: null },
    conversationAssigneeId: { type: Number, default: null },
    contactId: { type: Number, default: null },
  },
  emits: ['close', 'created'],
  data() {
    return {
      saving: false,
      ptBR: {
        formatLocale: {
          months: ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'],
          monthsShort: ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'],
          weekdays: ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'],
          weekdaysShort: ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'],
          weekdaysMin: ['D','S','T','Q','Q','S','S'],
          firstDayOfWeek: 0,
          firstWeekContainsDate: 1,
        },
        yearFormat: 'YYYY',
        monthBeforeYear: false,
      },
      form: {
        title: '',
        description: '',
        priority: 'medium',
        status_key: 'pending',
        assigned_to_user_id: this.conversationAssigneeId || null,
        due_at_local: '',
        reminder_offsets: [],
      },
      subtasks: [],
      selectedLabelIds: [],
      availableLabels: [],
      availableTemplates: [],
      selectedTemplateId: null,
      recurrenceEnabled: false,
      recurrenceFrequency: 'daily',
      reminderOptions: [
        { value: 5, label: '5 min' },
        { value: 15, label: '15 min' },
        { value: 60, label: '1 hora' },
        { value: 1440, label: '1 dia' },
      ],
    };
  },
  computed: { ...mapGetters({ agents: 'agents/getAgents' }) },
  mounted() {
    this.$nextTick(() => { this.$refs.titleInput?.focus(); });
    this.loadLabels();
    this.loadTemplates();
  },
  methods: {
    onClose() { this.$emit('close'); },
    async loadLabels() { try { const { data } = await TaskLabelsAPI.list(); this.availableLabels = Array.isArray(data) ? data : []; } catch (e) {} },
    async loadTemplates() { try { const { data } = await TaskTemplatesAPI.list(); this.availableTemplates = Array.isArray(data) ? data : []; } catch (e) {} },
    toggleLabel(id) { const idx = this.selectedLabelIds.indexOf(id); if (idx >= 0) this.selectedLabelIds.splice(idx, 1); else this.selectedLabelIds.push(id); },
    toggleReminder(v) { const idx = this.form.reminder_offsets.indexOf(v); if (idx >= 0) this.form.reminder_offsets.splice(idx, 1); else this.form.reminder_offsets.push(v); },
    applyTemplate() {
      if (!this.selectedTemplateId) return;
      const t = this.availableTemplates.find(x => x.id === this.selectedTemplateId);
      if (!t) return;
      this.form.title = this.form.title || t.name;
      this.form.description = this.form.description || t.description || '';
      this.form.priority = ['none','low','medium','high','urgent'][t.default_priority] || this.form.priority;
      this.subtasks = (t.default_subtasks || []).map((st, i) => ({ title: st.title || '', position: typeof st.position !== 'undefined' ? st.position : i }));
      this.selectedLabelIds = (t.default_label_ids || []).slice();
    },
    async submit() {
      if (!this.form.title.trim()) return;
      this.saving = true;
      try {
        let due_at = null;
        if (this.form.due_at_local) {
          const m = String(this.form.due_at_local).match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/);
          if (m) {
            const [_, d, mo, y, h, mi] = m;
            due_at = new Date().toISOString();
          }
        }
        const payload = {
          title: this.form.title.trim(),
          description: this.form.description || null,
          priority: this.form.priority,
          status_key: this.form.status_key,
          assigned_to_user_id: this.form.assigned_to_user_id || null,
          conversation_display_id: this.conversationDisplayId || null,
          contact_id: this.contactId || null,
          due_at: due_at,
          reminder_offsets: this.form.reminder_offsets,
          recurrence_rule: this.recurrenceEnabled ? { frequency: this.recurrenceFrequency, interval: 1 } : null,
          label_ids: this.selectedLabelIds,
        };
        const { data } = await TasksAPI.create(payload);
        for (const st of this.subtasks.filter(x => x.title.trim())) {
          try { await TaskSubtasksAPI.create(data.id, { title: st.title.trim(), position: st.position }); } catch (e) {}
        }
        useAlert('Tarefa criada');
        this.$emit('created', data);
        this.$emit('close');
      } catch (e) { useAlert('Erro ao criar tarefa'); }
      finally { this.saving = false; }
    },
  },
};
</script>

<style lang="scss" scoped>
.qt-container { width: 100%; display: flex; flex-direction: column; max-height: 88vh; }
.modal-header { padding: 12px 20px; border-bottom: 1px solid; @apply border-n-weak; }
.modal-header .header-content { display: flex; align-items: center; }
.modal-header .title-group .modal-title { font-size: 16px; font-weight: 600; @apply text-n-slate-12; }
.qt-body { padding: 16px 20px; display: flex; flex-direction: column; gap: 16px; flex: 1; overflow-y: auto; }
.link-pill { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 8px; font-size: 13px; @apply bg-n-alpha-1 text-n-brand border border-n-weak; }
.template-bar { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 8px; @apply bg-n-alpha-1; }
.template-bar .tplabel { font-size: 12px; @apply text-n-slate-10; }
.template-bar .form-input-sm { flex: 1; max-width: 240px; padding: 4px 24px 4px 8px; font-size: 12px; appearance: auto !important; -webkit-appearance: menulist !important; }
.qt-form { display: flex; flex-direction: column; gap: 16px; width: 100%; align-self: stretch; padding: 0; }
.form-row { display: grid; gap: 12px; }
.form-row-2 { grid-template-columns: 1fr 1fr; }
.form-row-3 { grid-template-columns: 1fr 1fr 1fr; }
.form-group { display: flex; flex-direction: column; gap: 6px; width: 100%; }
.form-group label { font-size: 12px; font-weight: 500; @apply text-n-slate-11; }
.form-group label .required { color: #ef4444; }
.form-group .form-input { width: 100%; padding: 8px 12px; border: 1px solid; border-radius: 6px; font-size: 14px; @apply border-n-weak text-n-slate-12; background: var(--color-background); } 
.form-group select.form-input { appearance: auto !important; -webkit-appearance: menulist !important; padding-right: 28px; }
.form-group .form-input:focus { outline: none; @apply border-n-brand; box-shadow: 0 0 0 2px rgba(31,147,255,0.1); }
.form-group textarea.form-input { min-height: 60px; resize: vertical; font-family: inherit; }
.form-group input[type='datetime-local'] { color-scheme: dark; }
.label-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.label-chip { font-size: 12px; padding: 3px 10px; border-radius: 6px; border: 1px solid; @apply border-n-weak text-n-slate-11; transition: all 120ms; }
.label-chip:hover { @apply border-n-strong; }
.label-chip.label-chip-active { font-weight: 600; }
.subtask-item { display: flex; align-items: center; gap: 8px; padding: 4px 0; }
.subtask-item .form-input-flat { border: 1px solid transparent; background: transparent; padding: 4px 8px; flex: 1; @apply text-n-slate-12; }
.subtask-item .form-input-flat:focus { outline: none; @apply border-n-brand; }
.subtask-item .icon-btn-sm { width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; border-radius: 4px; @apply text-n-slate-10; }
.subtask-item .icon-btn-sm:hover { @apply bg-n-alpha-1 text-red-500; }
.add-sub-btn { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; padding: 4px 8px; @apply text-n-brand; }
.add-sub-btn:hover { @apply bg-n-alpha-1; border-radius: 4px; }
.reminders { display: flex; gap: 12px; flex-wrap: wrap; }
.rmd-checkbox { display: inline-flex; align-items: center; gap: 4px; font-size: 13px; @apply text-n-slate-11; cursor: pointer; }
.recurrence { display: flex; align-items: center; gap: 8px; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 24px; border-top: 1px solid; @apply border-n-weak; width: 100%; margin-top: auto; }

.qt-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; font-size: 14px; font-weight: 500; border-radius: 8px; cursor: pointer; transition: all 150ms ease; border: 1px solid transparent; }
.qt-btn:disabled { cursor: not-allowed; opacity: 0.55; }
.qt-btn-secondary { @apply text-n-slate-11 border-n-weak; background: transparent; }
.qt-btn-secondary:hover:not(:disabled) { @apply bg-n-alpha-2 text-n-slate-12 border-n-strong; }
.qt-btn-primary { @apply bg-n-brand text-white; box-shadow: 0 1px 2px rgba(0,0,0,0.15); }
.qt-btn-primary:hover:not(:disabled) { filter: brightness(1.08); box-shadow: 0 2px 6px rgba(31,147,255,0.35); }
.qt-btn-primary:active:not(:disabled) { filter: brightness(0.95); }
.qt-datepicker { width: 100%; }
.qt-datepicker :deep(.mx-input) { width: 100%; padding: 8px 12px; border: 1px solid; border-radius: 6px; font-size: 14px; height: auto; box-shadow: none; @apply border-n-weak text-n-slate-12; background: var(--color-background); }
.qt-datepicker :deep(.mx-input:focus) { @apply border-n-brand; box-shadow: 0 0 0 2px rgba(31,147,255,0.1); }
.qt-datepicker :deep(.mx-icon-calendar) { @apply text-n-slate-10; }
:deep(.modal-container) { padding: 0 !important; width: 640px !important; max-width: 90vw !important; }
</style>
